import type { SelectOptionList } from '@cbhq/cds-web/alpha/select';
import type {
  CoinbaseEngUsersAccountManagementApiApiApprovalActionCatalogEntry as ApprovalActionCatalogEntry,
  CoinbaseEngUsersAccountManagementApiApiConsensusApproverRole as ConsensusApproverRole,
  CoinbaseEngUsersAccountManagementApiApiConsensusRuleDetail as ConsensusRuleDetail,
} from '@cbhq/identity-api-hooks/account-management/types';

import { ORG_SCOPED_RESOURCE_TYPE } from './constants';

type ApprovalRuleTarget = {
  entityId: string;
  ruleId?: string;
  version?: string;
};

// UI-ready approval rule with catalog and persisted configuration.
export type ApprovalRule = {
  id: string;
  product: string;
  resourceType: string;
  action: string;
  target: ApprovalRuleTarget;
  pendingApproval: boolean;
  isEnabled: boolean;
  approvers: string[];
  requirementCount: number;
  approverOptions: SelectOptionList<'multi', string>;
};

// Approval rules grouped under a product.
export type ApprovalProduct = {
  product: string;
  rules: ApprovalRule[];
};

// Entity IDs this view should read from and write to.
type ApprovalRuleEntityTargets = {
  legalEntityId: string;
  organizationId: string;
};

type BuildApprovalProductsArgs = ApprovalRuleEntityTargets & {
  actions: ApprovalActionCatalogEntry[] | undefined;
  rules: ConsensusRuleDetail[] | undefined;
  excludeResourceTypes?: readonly string[];
};

const SUPPORTED_PRODUCT_ORDER = ['account_management', 'cdp', 'shared'] as const;

// The consensus API returns "workspace" as the product id for account_management
// roles; normalize it so encoded role strings line up with the rest of the app.
function normalizeApproverProductId(productId: string | undefined): string {
  return productId === 'workspace' ? 'account_management' : productId ?? '';
}

export function encodeApproverRoleValue(role: ConsensusApproverRole, resourceType: string): string {
  if (!role.role) {
    throw new Error('Approver role is required.');
  }

  const product = normalizeApproverProductId(role.productId);
  const resourceKind = resourceType.includes('/') ? resourceType.split('/')[1] : resourceType;
  return `${product}/${resourceKind}:${role.role}`;
}

type DecodedApproverRole = {
  productId: string;
  role: string;
};

export function decodeApproverRoleValue(value: string): DecodedApproverRole {
  const roleSeparatorIndex = value.indexOf(':');
  const resourceSeparatorIndex = value.indexOf('/');

  if (
    resourceSeparatorIndex <= 0 ||
    roleSeparatorIndex <= resourceSeparatorIndex + 1 ||
    roleSeparatorIndex === value.length - 1
  ) {
    throw new Error(`Invalid approver role value: ${value}`);
  }

  const productKey = value.slice(0, resourceSeparatorIndex);

  return {
    productId: productKey === 'account_management' ? 'workspace' : productKey,
    role: value.slice(roleSeparatorIndex + 1),
  };
}

export function mapApproverRolesToOptions(
  roles: ConsensusApproverRole[] | undefined,
  resourceType: string,
): SelectOptionList<'multi', string> {
  const options: SelectOptionList<'multi', string> = [];
  const seenValues = new Set<string>();

  for (const role of roles ?? []) {
    const value = encodeApproverRoleValue(role, resourceType);
    if (!seenValues.has(value)) {
      seenValues.add(value);
      options.push({ value, label: role.role || value });
    }
  }

  return options;
}

// Matches a configured rule to a catalog action by resource type and actionKey.
export function matchRuleForAction(
  rules: ConsensusRuleDetail[] | undefined,
  entry: ApprovalActionCatalogEntry,
  entityId: string,
): ConsensusRuleDetail | undefined {
  if (!entry.actionKey || !entry.resourceType) {
    return undefined;
  }

  return (rules ?? []).find(
    (rule) =>
      rule.resourceType === entry.resourceType &&
      rule.actionKey === entry.actionKey &&
      rule.entityId === entityId,
  );
}

type ApprovalRuleConfiguration = Pick<
  ApprovalRule,
  'approvers' | 'isEnabled' | 'pendingApproval' | 'requirementCount'
>;

function buildRuleConfiguration(
  rule: ConsensusRuleDetail | undefined,
  resourceType: string,
): ApprovalRuleConfiguration {
  return {
    pendingApproval: rule?.status === 'pending_review',
    isEnabled: rule?.enabledStatus === 'active',
    approvers: (rule?.approverRoles ?? []).map((role) =>
      encodeApproverRoleValue(role, resourceType),
    ),
    requirementCount: rule?.requiredApprovals ?? 0,
  };
}

function getTargetEntityId(
  resourceType: string,
  { legalEntityId, organizationId }: ApprovalRuleEntityTargets,
): string {
  if (resourceType === ORG_SCOPED_RESOURCE_TYPE) {
    return organizationId;
  }

  return legalEntityId;
}

function buildApprovalRule(
  entry: ApprovalActionCatalogEntry,
  resourceType: string,
  product: string,
  rules: ConsensusRuleDetail[] | undefined,
  entityTargets: ApprovalRuleEntityTargets,
): ApprovalRule {
  const actionKey = entry.actionKey ?? '';
  const approverOptions = mapApproverRolesToOptions(entry.approverRoles, resourceType);
  const entityId = getTargetEntityId(resourceType, entityTargets);
  const matchedRule = matchRuleForAction(rules, entry, entityId);

  return {
    id: [product, resourceType, actionKey].join(':'),
    product,
    resourceType,
    action: actionKey,
    target: {
      entityId,
      ruleId: matchedRule?.ruleId,
      version: matchedRule?.version,
    },
    approverOptions,
    ...buildRuleConfiguration(matchedRule, resourceType),
  };
}

// Groups approval actions by product and overlays the configured rules.
export function buildApprovalProducts({
  actions,
  rules,
  excludeResourceTypes,
  ...entityTargets
}: BuildApprovalProductsArgs): ApprovalProduct[] {
  const excludedResourceTypes = new Set(excludeResourceTypes ?? []);

  const rulesByProduct = new Map<string, ApprovalRule[]>();

  for (const entry of actions ?? []) {
    const { actionKey, resourceType } = entry;
    if (actionKey && resourceType && !excludedResourceTypes.has(resourceType)) {
      const [product] = resourceType.split('/');
      const productRules = rulesByProduct.get(product) ?? [];
      productRules.push(buildApprovalRule(entry, resourceType, product, rules, entityTargets));
      rulesByProduct.set(product, productRules);
    }
  }

  return SUPPORTED_PRODUCT_ORDER.flatMap((product) => {
    const productRules = rulesByProduct.get(product);
    return productRules?.length ? [{ product, rules: productRules }] : [];
  });
}
